import {createModel} from '@rematch/core';
import {isAxiosError} from 'axios';

import {TProfile, requestProfile} from '~/services/account';
import {
  TNotificationsResponse,
  fetchUserNotifications,
  setUserNotificationAsReaded,
  removeNotification,
  setAllUserNotificationsAsReaded,
  removeAllNotifications,
} from '~/services/hot-api/notifications';

import type {RootModel} from '.';

export interface ProfileState {
  data: TProfile | null;
  notifications: {
    response: TNotificationsResponse;
    timerId: NodeJS.Timer | null;
  };
  isGuest: boolean;
}

const REFRESH_NOTIFICATIONS_TIMER = 1000 * 60 * 5; // 5 min

const defaultNotificationsState: ProfileState['notifications'] = {
  response: {
    total: 0,
    unviewed: 0,
    items: [],
  },
  timerId: null,
};

const defaultState: ProfileState = {
  data: null,
  notifications: defaultNotificationsState,
  isGuest: true,
};

export const profile = createModel<RootModel>()({
  state: defaultState,
  reducers: {
    setGuest: (state: ProfileState, isGuest: boolean) => ({
      ...state,
      isGuest,
    }),
    setProfileData: (state: ProfileState, data: any) => ({
      ...state,
      data,
    }),
    setNotifications: (state: ProfileState, notifications: TNotificationsResponse) => ({
      ...state,
      notifications: {
        ...state.notifications,
        response: notifications,
      },
    }),
    setNotificationTimer: (state, timerId: NodeJS.Timer | null) => ({
      ...state,
      notifications: {
        ...state.notifications,
        timerId,
      },
    }),
    markNotificationAsReaded: (state: ProfileState, notificationId: number) => ({
      ...state,
      notifications: {
        ...state.notifications,
        response: {
          ...state.notifications.response,
          items: state.notifications.response.items.map((notification) => {
            if (notificationId === notification.id) {
              return {
                ...notification,
                readed: true,
              };
            }
            return notification;
          }),
          unviewed: state.notifications.response.unviewed - 1,
        },
      },
    }),
    removeSignleNotification: (state, notificationId: number) => {
      const {response} = state.notifications;
      const notification = response.items.find(({id}) => id === notificationId);

      if (!notification) {
        return state;
      }
      const isNotificationWasReaded = notification.viewed || notification.readed;

      return {
        ...state,
        notifications: {
          ...state.notifications,
          response: {
            ...response,
            items: response.items.filter(({id}) => id !== notificationId),
            total: response.total - 1,
            unviewed: isNotificationWasReaded ? response.unviewed : response.unviewed - 1,
          },
        },
      };
    },
    resetNotificationsState: (state) => {
      return {
        ...state,
        notifications: defaultNotificationsState,
      };
    },
  },

  effects: (dispatch) => {
    window.addEventListener('DOMContentLoaded', () => {
      dispatch.profile.getCurrentUserInfo();
    });

    return {
      async getProfile(): Promise<void> {
        try {
          const response = await requestProfile();

          dispatch.profile.setProfileData(response.data);
        } catch (e) {
          console.error(e);
        }
      },
      async loadNotifications() {
        try {
          const notifications = await fetchUserNotifications();

          dispatch.profile.setNotifications(notifications.data);
        } catch (e) {
          if (isAxiosError(e) && e.response?.status === 401) {
            dispatch.profile.stopNotificationsPulling();
          }
        }
      },
      async setNotificationAsRead(notificationId: number, state) {
        // Negative counter protection
        const notification = state.profile.notifications.response.items.find(
          ({id}) => id === notificationId
        );

        if (!notification || notification.readed || notification.viewed) {
          return;
        }

        // call to mark on remote
        try {
          await setUserNotificationAsReaded(notificationId);
        } catch (e) {
          console.error(e);
        }

        // mark locally
        dispatch.profile.markNotificationAsReaded(notificationId);
      },
      async removeNotification(notificationId: number) {
        // remove locally
        dispatch.profile.removeSignleNotification(notificationId);

        // remove on remote
        try {
          await removeNotification(notificationId);
        } catch (e) {
          console.error(e);
        }
      },
      async setAllNotifactionsAsReaded(_: void, state) {
        // error handled in ui with showing error
        // call to mark on remote
        await setAllUserNotificationsAsReaded();
        const viewed = new Date().toISOString();
        // mark locally
        dispatch.profile.setNotifications({
          total: state.profile.notifications.response.total,
          unviewed: 0,
          items: state.profile.notifications.response.items.map((item) => ({
            ...item,
            viewed,
          })),
        });
      },
      async removeAllNotifications() {
        // error handled in ui with showing error
        await removeAllNotifications();
        // remove locally
        dispatch.profile.resetNotificationsState();
      },
      async loadMoreNotifications(_: void, state) {
        try {
          const currentNotificationsList = state.profile.notifications.response.items;
          const {data} = await fetchUserNotifications({
            offset: currentNotificationsList.length,
          });

          dispatch.profile.setNotifications({
            ...data,
            items: [...currentNotificationsList, ...data.items],
          });
        } catch (error) {
          dispatch.profile.setNotifications(state.profile.notifications.response);
        }
      },
      async startNotificationsPulling(_: void, state) {
        const {timerId} = state.profile.notifications;

        if (timerId) {
          clearInterval(timerId);
        }

        const newTimerId = setInterval(() => {
          dispatch.profile.loadNotifications();
        }, REFRESH_NOTIFICATIONS_TIMER);

        dispatch.profile.setNotificationTimer(newTimerId);
      },
      async stopNotificationsPulling(_: void, state) {
        const {timerId} = state.profile.notifications;

        if (timerId) {
          clearInterval(timerId);
          dispatch.profile.setNotificationTimer(null);
        }
      },
      async getCurrentUserInfo(_: void, state) {
        const {isGuest} = state.app.data.account;

        dispatch.profile.setGuest(isGuest);

        if (!isGuest) {
          dispatch.profile.getProfile();
        }
      },
    };
  },
});
